import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { GrowthBook, useFeatureIsOn, useGrowthBook } from './growthbook';
import { IAuthState } from './createAuthContext';
import {
  ICustomerAddress,
  IUseLocalStorage,
  ShoppingCartProductType,
} from '@monofront/types';
import { logger } from '@monofront/logging';
import {
  FEATURE_FLAG_FRESH_ELIGIBILITY,
  FEATURE_FLAG_ULTRA_FRESH_ELIGIBILITY,
  FRESH_USER,
  ULTRA_FRESH_USER,
} from '../constants';
import { needEligibilityCheck } from '../utils';
import { ICustomerCRUD } from '../interfaces';

/**
 * `null` eligibility represent the "unknown state" (we don't know
 * the address yet).
 */
type IEligibility = boolean | null;

export type IEligibilities = {
  fresh: IEligibility;
  ultraFresh: IEligibility;
};

type IEligibilityContext = {
  status: EligibilityStatus;
  address: ICustomerAddress | null;
  eligibility: {
    fresh: IEligibility;
    ultraFresh: IEligibility;
  };
  /**
   * @throws error when eligibility can't be checked
   */
  checkEligibility: (address: ICustomerAddress | null) => IEligibilities;
  /**
   * Helper to check if an eligibility needs to be checked for a specific product.
   * It adds current eligibility tags if present.
   */
  checkNeedEligibility: (product: ShoppingCartProductType) => boolean;
  /**
   * Stateless helper to check eligibility for a specific address.
   */
  checkAddressEligibility: (
    growthbook: GrowthBook | undefined,
    address: ICustomerAddress | null,
  ) => IEligibilities;
};

type IEligibilityProviderProps = {
  /**
   * TODO: LF-1675 - shared useAuthentication hook
   */
  useAuthentication: () => IAuthState;
  useLocalStorage: IUseLocalStorage;
  customerCRUD: ICustomerCRUD;
};

type EligibilityState = {
  customerUid: string | null;
  status: EligibilityStatus;
  address: ICustomerAddress | null;
  eligibility: IEligibilities;
};

export enum EligibilityStatus {
  /**
   * State is not initialised yet, status is unreliable.
   */
  INIT = 'INIT',
  /**
   * State is unreliable: it may contain the previous state's
   * eligibilities.
   * Loading status may be displayed to the user.
   */
  LOADING = 'LOADING',
  /**
   * State is safe to be accessed and relied upon.
   */
  READY = 'READY',
}

export type IEligibilityStorage = Pick<
  IEligibilityContext,
  'address' | 'eligibility'
>;

export const DEFAULT_ELIGIBILITY_CONTEXT_STATE: Pick<
  IEligibilityContext,
  'address' | 'eligibility' | 'status'
> = {
  status: EligibilityStatus.INIT,
  address: null,
  eligibility: {
    fresh: null,
    ultraFresh: null,
  },
};

const DEFAULT_CONTEXT_STATE: IEligibilityContext = {
  ...DEFAULT_ELIGIBILITY_CONTEXT_STATE,
  checkEligibility: () => DEFAULT_ELIGIBILITY_CONTEXT_STATE.eligibility,
  checkNeedEligibility: () => false,
  checkAddressEligibility: () => DEFAULT_ELIGIBILITY_CONTEXT_STATE.eligibility,
};

const EligibilityContext = React.createContext<IEligibilityContext>(
  DEFAULT_CONTEXT_STATE,
);

const checkAddressEligibility = (
  growthbook: GrowthBook | undefined,
  address: ICustomerAddress | null,
) => {
  if (!growthbook) {
    logger.error('Missing growthbook provider!');
    throw new Error("Can't check address eligibilities!");
  }

  growthbook.setAttributes({
    ...growthbook.getAttributes(),
    postalCode: address?.postalCode ?? null,
    country: address?.country ?? null,
  });

  if (!address || !address.country || !address.postalCode) {
    return DEFAULT_ELIGIBILITY_CONTEXT_STATE.eligibility;
  }

  return {
    fresh: growthbook.isOn(FEATURE_FLAG_FRESH_ELIGIBILITY),
    ultraFresh: growthbook.isOn(FEATURE_FLAG_ULTRA_FRESH_ELIGIBILITY),
  };
};

export const EligibilityProvider = ({
  useAuthentication,
  useLocalStorage,
  customerCRUD,
  children,
}: PropsWithChildren<IEligibilityProviderProps>) => {
  const growthbook = useGrowthBook();
  const { customer } = useAuthentication();
  const [storageValue, setStorageValue] = useLocalStorage<IEligibilityStorage>(
    'lf_address_eligibility',
    DEFAULT_ELIGIBILITY_CONTEXT_STATE,
  );
  const [{ customerUid, status, address, eligibility }, setEligibilityState] =
    useState<EligibilityState>({
      customerUid: customer?.uid ?? null,
      status: EligibilityStatus.INIT,
      address: storageValue.address,
      eligibility: storageValue.eligibility,
    });

  // The storage value is not always retrieved at the init of the state. Leaving the status to INIT as it is an asynchronous initialisation
  useEffect(() => {
    if (
      status === EligibilityStatus.INIT &&
      address === null &&
      storageValue.address &&
      !customer?.uid
    ) {
      setEligibilityState((props) => ({
        ...props,
        address: storageValue.address,
        eligibility: storageValue.eligibility,
      }));
    }
  }, [status, storageValue, customer?.uid]);

  const isFreshEligible = useFeatureIsOn(FEATURE_FLAG_FRESH_ELIGIBILITY);
  const isUltraFreshEligible = useFeatureIsOn(
    FEATURE_FLAG_ULTRA_FRESH_ELIGIBILITY,
  );

  useEffect(() => {
    logger.info('Eligibility: feature flags updated', {
      address,
      eligibility,
    });

    const newEligibility = checkAddressEligibility(growthbook, address);

    setEligibilityState((state) => {
      if (
        newEligibility.fresh === state.eligibility.fresh &&
        newEligibility.ultraFresh === state.eligibility.ultraFresh
      ) {
        // don't trigger a new render if nothing has changed
        return state;
      }

      return {
        ...state,
        eligibility: newEligibility,
      };
    });
  }, [isFreshEligible, isUltraFreshEligible, address]);

  const checkNeedEligibility = useMemo(() => {
    const tags = [] as Array<string>;

    if (eligibility.fresh) {
      tags.push(FRESH_USER);
    }
    if (eligibility.ultraFresh) {
      tags.push(ULTRA_FRESH_USER);
    }

    logger.debug(
      'Eligibility',
      'checking if eligibility needs to be verified',
      {
        tags,
        eligibility,
      },
    );

    return needEligibilityCheck.bind(null, tags);
  }, [eligibility]);

  // Sync local storage to remote storage
  useEffect(() => {
    // If we have an address saved locally, override the one in firebase
    // with this one, and erase the local one.
    if (storageValue.address != null && customer) {
      logger.debug(
        'Eligibility',
        'User logged in: sync eligibility from localStorage to Firestore and delete localStorage',
      );

      void customerCRUD.update(customer.uid, {
        address: {
          // Pass customer's address to get firstName and lastName if present (from default address)
          // This prevents unnecessary update going through Agora because we remove the names by going
          // through the Eligibility Modal.
          ...customer.address,
          ...storageValue.address,
        },
      });

      setStorageValue(DEFAULT_ELIGIBILITY_CONTEXT_STATE);
    } else if (customer?.address) {
      logger.debug(
        'Eligibility',
        'User logged in: sync eligibility from Firestore to local state',
      );

      setEligibilityState({
        customerUid: customer?.uid ?? null,
        status: EligibilityStatus.READY,
        address: customer.address,
        eligibility: checkAddressEligibility(growthbook, customer.address),
      });
    }
  }, [
    storageValue,
    customer?.uid,
    setStorageValue,
    customer?.address,
    setEligibilityState,
  ]);

  useEffect(() => {
    const hasCustomerChanged = (customer?.uid ?? null) !== customerUid;
    const isCustomerNotAnonymousBefore = customerUid != null;

    logger.info('Eligibility: eligibility updated', {
      address,
      eligibility,
    });

    if (hasCustomerChanged && isCustomerNotAnonymousBefore) {
      logger.debug('Eligibility', 'user changed: delete local state');
      setEligibilityState({
        customerUid: customer?.uid ?? null,
        ...DEFAULT_ELIGIBILITY_CONTEXT_STATE,
      });

      return;
    }

    if (customer && !hasCustomerChanged) {
      logger.debug('Eligibility', 'new address: sync address to Firestore');

      if (address?.country && address?.postalCode) {
        const nextAddress: Partial<ICustomerAddress> = {
          ...(address.street ? { street: address.street } : {}),
          ...(address.city ? { city: address.city } : {}),
          ...(address.postalCode ? { postalCode: address.postalCode } : {}),
          ...(address.country ? { country: address.country } : {}),
        };

        // Fire-and-forget; add error logging if the update fails
        customerCRUD
          .update(customer.uid, {
            address: { ...(customer.address ?? {}), ...nextAddress },
          })
          .catch((err: unknown) =>
            logger.error('Eligibility: failed to sync address to Firestore', {
              err,
            }),
          );
      }

      return;
    }

    if (!customer && address !== null) {
      logger.debug('Eligibility', 'new address: sync address to localStorage');

      setStorageValue({ address, eligibility });
    }
  }, [address, eligibility, customer?.uid]);

  const checkEligibility = useCallback((address: ICustomerAddress | null) => {
    const eligibility = checkAddressEligibility(growthbook, address);

    setEligibilityState({
      customerUid: customer?.uid ?? null,
      status: EligibilityStatus.READY,
      address,
      eligibility,
    });

    return eligibility;
  }, []);

  const value = useMemo(
    () => ({
      status,
      address,
      eligibility,
      checkEligibility,
      checkNeedEligibility,
      checkAddressEligibility,
    }),
    [status, address, eligibility, checkEligibility, checkNeedEligibility],
  );

  return (
    <EligibilityContext.Provider value={value}>
      {children}
    </EligibilityContext.Provider>
  );
};

export const useEligibility = () => {
  return useContext(EligibilityContext);
};
